<script lang="ts" setup>
import { useFiltersCache } from "@/composables";
import SearchField from "@/components/inputs/SearchField.vue";
import DispatcherSelect from "@/components/selects/DispatcherSelect.vue";
import TableFilterRow from "@/components/tables/TableFilterRow.vue";
import TableFilter from "@/components/tables/TableFilter.vue";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import CustomerSelect from "@/views/orders/components/CustomerSelect.vue";
import type { TripMonitorDispatchStatus, TripMonitorSearchParams } from "@/api/trips/dto/trip-monitor";
import { RedispatchStateMap, RouteStopStatus, RouteStopStatusOptions } from "@/constants/trip";
import CarriesSelect from "@/components/selects/CarriesSelect.vue";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import { TruckTypeList } from "@/constants/truck";
import { OrderStopTypesOptions } from "@/constants/order";

const defaultFilterState: TripMonitorSearchParams = {
  refNumber: null,
  bookedBy: null,
  truckNumber: null,
  billToCompanyId: null,
  progresses: null,
  orderNumber: null,
  carrier: null,
  dispatchStatus: null,
  arrivalDate: null,
  travelOrderId: null,
  truckType: null,
};

const { getSearchParams, setSearchParams } = useFiltersCache();
const searchParamsKey = "trip-monitor";

defineModel<number>("height", { default: 0 });

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "search", params: any): void;
  (e: "update:height", value: number): void;
}>();

const params = ref(defaultFilterState);
const tableFilterRef = useTemplateRef<HTMLDivElement>("tableFilterRef");

const progressOptions = computed(() => {
  return [
    ...RouteStopStatusOptions.filter((option) => option.id !== RouteStopStatus.inRoute),
    ...OrderStopTypesOptions,
  ];
});

const dispatchStatusOptions = computed<TripMonitorDispatchStatus[]>(() => {
  return [
    {
      id: "not-confirmed-dispatch",
      name: "Not confirmed",
    },
    ...RedispatchStateMap,
  ];
});

const activeParams = computed(() => {
  return Object.fromEntries(Object.entries(params.value).filter(([key, value]) => Boolean(value)));
});

async function updateFilterHeight() {
  await nextTick();
  if (tableFilterRef.value) {
    emit("update:height", tableFilterRef.value.offsetHeight);
  }
}

function search() {
  setSearchParams(searchParamsKey, activeParams.value);
  emit("search", activeParams.value);
}

function clear() {
  params.value = { ...defaultFilterState };
  search();
}

function expand() {
  updateFilterHeight();
}

onMounted(() => {
  const savedParams = getSearchParams(searchParamsKey);

  if (Object.keys(savedParams).length > 0) {
    params.value = savedParams;
    search();
  }
});

watch(
  () => props.visible,
  () => {
    const animationDuration = 700;

    setTimeout(() => {
      updateFilterHeight();
    }, animationDuration);
  },
  {
    immediate: true,
  },
);

onMounted(() => {
  search();
});
</script>

<template>
  <div ref="tableFilterRef">
    <table-filter
      :filters="params"
      :header-filter-keys="['refNumber', 'bookedBy', 'number', 'billToCustomer', 'progress']"
      @apply="search"
      @clear="clear"
      @expand="expand"
    >
      <template #header>
        <table-filter-row :divider="false" icon="mdi-filter-variant" label="Main info">
          <v-col>
            <search-field v-model="params.refNumber" label="Ref number" name="refNumber" @search="search" />
          </v-col>
          <v-col>
            <dispatcher-select v-model="params.bookedBy" label="Booked by" multiple name="bookedBy" @search="search" />
          </v-col>
          <v-col>
            <search-field v-model="params.truckNumber" label="Truck number" name="number" @search="search" />
          </v-col>
          <v-col>
            <customer-select
              v-model="params.billToCompanyId"
              :items="[]"
              clearable
              hide-details
              label="Bill to company"
              name="billToCustomer"
              @update:model-value="search"
            />
          </v-col>
          <v-col>
            <omni-select
              v-model="params.progresses"
              :items="progressOptions"
              clearable
              label="Progress status"
              multiple
              name="progress"
              @update:model-value="search"
            />
          </v-col>
        </table-filter-row>
      </template>
      <table-filter-row :divider="false" icon="mdi-information-outline" label="Other">
        <v-col>
          <search-field v-model="params.orderNumber" label="Order number" name="orderNumber" @search="search" />
        </v-col>
        <v-col>
          <carries-select v-model="params.carrier" />
        </v-col>
        <v-col>
          <omni-select v-model="params.truckType" :items="TruckTypeList" label="Truck type" multiple @search="search" />
        </v-col>
        <v-col>
          <search-field
            v-model="params.travelOrderId"
            label="Travel order number"
            name="travelOrderId"
            @search="search"
          />
        </v-col>
        <v-col>
          <omni-select
            v-model="params.dispatchStatus"
            :items="dispatchStatusOptions"
            clearable
            data-qa="dispatch-status-field"
            label="Dispatch status"
            name="dispatchStatus"
            @update:model-value="search"
          />
        </v-col>
        <v-col>
          <date-picker
            v-model="params.arrivalDate"
            :items="[]"
            clearable
            label="Arrival date"
            name="arrivalDate"
            @update:model-value="search"
          />
        </v-col>
      </table-filter-row>
    </table-filter>
  </div>
</template>

<style lang="scss" scoped>
.dimension-part {
  position: relative;

  &:after {
    content: "х";
    position: absolute;
    top: 50%;
    right: -4px;
    transform: translateY(-50%);
  }
}
</style>
